import { ThoughtspotIntegrationService } from '@mode/shared/util-thoughtspot-integrated';
import { CommonModule, DOCUMENT, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';

import { CdkTableModule } from '@angular/cdk/table';
import { IconComponent, SearchInputComponent, trackByFactory } from '@mode/capra';
import { Spreadsheet, Worksheet } from '@mode/dataset/excel/contract';

@Component({
  standalone: true,
  selector: 'mode-excel-sheets-browser',
  imports: [CdkTableModule, CommonModule, DatePipe, IconComponent, SearchInputComponent],
  templateUrl: './excel-sheets-browser.component.html',
  styleUrls: ['./excel-sheets-browser.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExcelSheetsBrowserComponent implements OnChanges {
  @Input({ required: true }) spreadsheets: readonly Readonly<Spreadsheet>[] = [];
  @Input({ required: true }) worksheets: readonly Readonly<Worksheet>[] = [];
  @Input({ required: true }) searchText = '';
  @Input({ required: true }) helpLink = '';
  @Input() moreSheetsAvailable = false;
  @Output() spreadsheetChange = new EventEmitter<Readonly<Spreadsheet>>();
  @Output() worksheetChange = new EventEmitter<Readonly<Worksheet> | null>();
  @Output() searchTextChange = new EventEmitter<string>();
  @Output() loadMoreSheets = new EventEmitter<void>();

  showBrowser = true;
  selectedSpreadsheetRow = new SelectionModel<Spreadsheet>(false, []);
  selectedSpreadsheet: Readonly<Spreadsheet> | null = null;
  selectedWorksheetRow = new SelectionModel<Worksheet>(false, []);
  selectedWorksheet: Readonly<Worksheet> | null = null;
  worksheetsLoading = false;
  loadMoreSheetsEnabled = true;
  readonly spreadsheetColumns: readonly (keyof Spreadsheet)[] = ['name', 'owner', 'lastOpened'] as const;
  readonly worksheetColumns: readonly (keyof Spreadsheet)[] = ['name'] as const;
  readonly trackByFn = trackByFactory<Spreadsheet | Worksheet>(({ id }) => id);
  readonly isAnalystStudio = this.thoughtspotIntegrationService.currOrgIntegrated();

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private thoughtspotIntegrationService: ThoughtspotIntegrationService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.['worksheets']?.currentValue) {
      this.worksheetsLoading = false;

      const firstWorksheet = this.worksheets[0];
      if (firstWorksheet) {
        setTimeout(() => this.selectWorksheet(firstWorksheet));
      }
    }

    if (changes?.['spreadsheets']?.currentValue) {
      this.loadMoreSheetsEnabled = true;

      const selectedSpreadsheet = this.selectedSpreadsheet;
      if (selectedSpreadsheet) {
        const latestSelectedSpreadsheet = this.spreadsheets.find(({ id }) => id === selectedSpreadsheet.id);
        if (latestSelectedSpreadsheet) {
          this.selectedSpreadsheetRow.select(latestSelectedSpreadsheet);
          setTimeout(() => this._document.querySelector('tr.selected')?.scrollIntoView({ block: 'center' }));
        }
      }
    }
  }

  selectSpreadsheet(spreadsheet: Readonly<Spreadsheet>) {
    this.selectedSpreadsheetRow.select(spreadsheet);
    this.selectedSpreadsheet = spreadsheet;
    this.selectedWorksheetRow.clear();
    this.selectedWorksheet = null;
    this.spreadsheetChange.emit(spreadsheet);
    this.worksheetChange.emit(null);
    this.worksheetsLoading = true;
  }

  selectWorksheet(worksheet: Readonly<Worksheet>) {
    this.selectedWorksheetRow.select(worksheet);
    this.selectedWorksheet = worksheet;
    this.worksheetChange.emit(worksheet);
  }

  showMoreSpreadsheets() {
    this.loadMoreSheets.emit();
    this.loadMoreSheetsEnabled = false;
  }
}
